import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import THEME from '../../styles/theme';
import { useConfirmDialogStore } from '../../store/useConfirmDialogStore';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -45%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${THEME.zIndex.modal};
  animation: ${fadeIn} ${THEME.transitionFast};
`;

const Modal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: ${THEME.panelBackground};
  border: 1px solid ${THEME.border};
  border-radius: 16px;
  padding: 0;
  width: 90%;
  max-width: 403px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  animation: ${slideUp} ${THEME.transitionMedium};
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${THEME.spacing.lg} ${THEME.spacing.xl};
  border-bottom: 1px solid ${THEME.border};
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: ${THEME.fontSize.lg};
  color: ${THEME.textPrimary};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: ${THEME.spacing.sm};
`;

const WarningIcon = styled.span`
  font-size: ${THEME.fontSize.xl};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${THEME.textSecondary};
  font-size: ${THEME.fontSize.xl};
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all ${THEME.transitionFast};

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: ${THEME.textPrimary};
  }
`;

const ModalBody = styled.div`
  padding: ${THEME.spacing.xl};
  color: ${THEME.textSecondary};
  font-size: ${THEME.fontSize.lg};
  line-height: 1.5;
  white-space: pre-line;
  text-align: center;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${THEME.spacing.md};
  padding: ${THEME.spacing.lg} ${THEME.spacing.xl};
  border-top: 1px solid ${THEME.border};
`;

const Button = styled.button`
  padding: ${THEME.spacing.sm} ${THEME.spacing.lg};
  border-radius: 8px;
  font-size: ${THEME.fontSize.md};
  font-weight: 500;
  cursor: pointer;
  transition: all ${THEME.transitionFast};
  border: none;
  min-width: 80px;

  ${props => props.variant === 'primary' && `
    background: ${THEME.buttonPrimary};
    color: white;

    &:hover {
      background: ${THEME.buttonHover};
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  `}

  ${props => props.variant === 'secondary' && `
    background: transparent;
    color: ${THEME.textSecondary};
    border: 1px solid ${THEME.border};

    &:hover {
      background: rgba(255, 255, 255, 0.05);
      color: ${THEME.textPrimary};
      border-color: ${THEME.textSecondary};
    }
  `}
`;

const ConfirmDialog = () => {
  const { isOpen, config, confirm, cancel } = useConfirmDialogStore();

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        cancel();
      } else if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        confirm();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, confirm, cancel]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      cancel();
    }
  };

  const title = config.title || 'Confirm Action';
  const message = config.message || 'Are you sure you want to continue?';
  const confirmLabel = config.confirmLabel || 'Continue';
  const cancelLabel = config.cancelLabel || 'Cancel';
  const showWarningIcon = config.variant === 'warning' || config.variant === 'danger';
  const isAlertMode = config.mode === 'alert';

  return (
    <Overlay onClick={handleOverlayClick}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            {showWarningIcon && <WarningIcon>⚠️</WarningIcon>}
            {title}
          </ModalTitle>
          <CloseButton onClick={cancel} aria-label="Close">
            ✕
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          {message}
        </ModalBody>
        <ModalFooter>
          {!isAlertMode && (
            <Button variant="secondary" onClick={cancel}>
              {cancelLabel}
            </Button>
          )}
          <Button variant="primary" onClick={confirm}>
            {confirmLabel}
          </Button>
        </ModalFooter>
      </Modal>
    </Overlay>
  );
};

export default ConfirmDialog;
